import React, { useEffect, useRef, useState } from "react";
import { Accordion, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPermissions,
  fetchPermissionsById,
} from "slices/permissions/thunk";

import { Axios } from "api/axios";
import CancelBackButton from "components/Buttons/CancelBackButton";
import SubmitButton from "components/Buttons/SubmitButton";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { showCustomSwal } from "utils/swalHelper";

interface PermissionTabProps {
  id?: string;
}

const PermissionTab: React.FC<PermissionTabProps> = ({ id }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const [openAccordion, setOpenAccordion] = useState<string | undefined>();
  const [selected, setSelected] = useState<any>({});
  const [hasExistingPermissions, setHasExistingPermissions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { modules, loading, error } = useSelector(
    (state: any) => state.Permission
  );
  // Extract user ID from localStorage or props
  // 
  // WHERE permission_user IS SET:
  // - Set in BasicDetailsTab.tsx (line 259-261) after creating a new user:
  //   localStorage.setItem("permission_user", JSON.stringify(response?.data?.data?.user_id))
  // - This allows the permissions tab to know which user was just created
  // - It's removed in the cleanup function below (line 91) when component unmounts or user changes
  //
  // USAGE:
  // - When editing: use `id` prop from URL params (passed from UserForm.tsx)
  // - When creating: use `permission_user` from localStorage (set after user creation)
  // - Priority: id prop > localStorage > null
  //
  const local_data_str = localStorage?.getItem("permission_user");
  let local_data: any = null;
  try {
    if (local_data_str) {
      local_data = JSON.parse(local_data_str);
    }
  } catch (e) {
    console.error("Error parsing permission_user from localStorage:", e);
  }
  
  // Determine user ID: prefer id prop (when editing), then local_data (when creating new user)
  // local_data is stored as JSON.stringify(user_id), so it's a string after parsing
  const getUserFromLocalData = () => {
    if (typeof local_data === 'string') {
      return local_data;
    }
    if (typeof local_data === 'object' && local_data !== null) {
      return local_data.user_id || local_data.id;
    }
    return null;
  };
  
  const user = id || getUserFromLocalData() || null;
  const authUser = JSON.parse(localStorage?.getItem("authUser") || "{}");
  
  console.log("PermissionTab - id prop:", id, "local_data:", local_data, "resolved user:", user);

  // Track if permissions have been fetched for the current user
  const permissionsFetchedRef = useRef<string | null>(null);

  useEffect(() => {
    dispatch(fetchPermissions());
  }, [dispatch]);

  useEffect(() => {
    const local_data_str = localStorage.getItem("permission_user");
    let local_data: any = null;
    try {
      if (local_data_str) {
        local_data = JSON.parse(local_data_str);
      }
    } catch (e) {
      console.error("Error parsing permission_user from localStorage:", e);
    }

    const isCreating = !id && !local_data;

    if (isCreating) {
      setSelected({});
      setHasExistingPermissions(false);
      setOpenAccordion(undefined);
      // Reset the fetch ref when creating a new user
      permissionsFetchedRef.current = null;
    } else if (id) {
      // Reset the fetch ref when user ID changes (editing different user)
      permissionsFetchedRef.current = null;
    }
    return () => {
      localStorage.removeItem("permission_user");
    };
  }, [id]);

  useEffect(() => {
    // Only fetch permissions once when user and modules are ready
    // Skip if already fetched for this user or if user/modules not ready
    if (!user || modules.length === 0) {
      console.log("Skipping permission fetch - user:", user, "modules.length:", modules.length);
      return;
    }

    // Ensure user is a string (handle case where it might be an object)
    const userId = typeof user === 'string' ? user : (user?.user_id || user?.id || String(user));
    if (!userId) {
      console.log("No valid user ID found:", user);
      return;
    }

    // Check if we've already fetched permissions for this user
    if (permissionsFetchedRef.current === userId) {
      console.log("Already fetched permissions for user:", userId);
      return;
    }

    // Mark that we're fetching for this user
    permissionsFetchedRef.current = userId;
    console.log("Fetching permissions for user:", userId);

    dispatch(fetchPermissionsById(userId))
      .unwrap()
      .then((resp: any) => {
        console.log("Permission fetch response: ", resp);
        
        // Handle different response structures
        // Backend returns: { status, success, message, data: { user_id, apps_permissions } }
        // Thunk extracts: response.data.data which should be { user_id, apps_permissions }
        const userPermissions = resp?.apps_permissions || resp?.data?.apps_permissions || [];
        
        console.log("Extracted userPermissions: ", userPermissions);
        
        if (userPermissions.length > 0) {
          setHasExistingPermissions(true);
        } else {
          setHasExistingPermissions(false);
        }

        const normalized: any = {};
        let firstFullyCheckedApp: string | undefined;

        modules.forEach((modApp: any) => {
          const appName = modApp.app_name;
          normalized[appName] = {};

          // Case-insensitive matching for app name (backend stores as-is, but we match case-insensitively for compatibility)
          const matchedApp = userPermissions.find(
            (ap: any) => ap.apps_name?.toLowerCase() === appName?.toLowerCase()
          );

          let appFullyChecked = true;

          Object.entries(modApp.module_name_perm).forEach(
            ([moduleName, actions]: [string, any]) => {
              normalized[appName][moduleName] = {};
              
              // Case-insensitive matching for module name (backend stores as-is, but we match case-insensitively for compatibility)
              const matchedModule = matchedApp?.modules_permissions?.find(
                (m: any) => m.modules_name?.toLowerCase() === moduleName?.toLowerCase()
              );

              let moduleFullyChecked = true;

              actions.forEach((action: string) => {
                // Case-insensitive matching for action names (backend stores as-is, but we match case-insensitively for compatibility)
                const permissions = matchedModule?.permissions || {};
                
                // Try exact match first, then case-insensitive match
                let isChecked = permissions[action] === true;
                if (!isChecked) {
                  // Fallback to case-insensitive match
                  const actionLower = action.toLowerCase();
                  for (const [key, value] of Object.entries(permissions)) {
                    if (key.toLowerCase() === actionLower && value === true) {
                      isChecked = true;
                      break;
                    }
                  }
                }
                
                normalized[appName][moduleName][action] = isChecked;

                if (!isChecked) {
                  moduleFullyChecked = false;
                  appFullyChecked = false;
                }
              });
            }
          );

          if (appFullyChecked && !firstFullyCheckedApp) {
            firstFullyCheckedApp = appName;
          }
        });

        console.log("Normalized permissions: ", normalized);
        setSelected(normalized);

        // Open app accordion if entire app fully checked
        if (firstFullyCheckedApp) {
          setOpenAccordion(firstFullyCheckedApp);
        }
      })
      .catch((error: any) => {
        console.error("Error fetching permissions:", error);
        setHasExistingPermissions(false);
        // Reset the ref on error so we can retry
        permissionsFetchedRef.current = null;
        // Don't show error toast if it's just "no permissions found" (404 is expected for new users)
        if (error?.response?.status !== 404) {
          toast.error("Failed to fetch user permissions");
        }
      });
  }, [user, modules, dispatch]);

  const toggleAccordion = (id: string) => {
    setOpenAccordion(openAccordion === id ? undefined : id);
  };

  const handlePermissionChange = (
    app: string,
    module: string,
    action: string,
    checked: boolean
  ) => {
    setSelected((prev: any) => {
      const updated = structuredClone(prev || {});
      if (!updated[app]) updated[app] = {};
      if (!updated[app][module]) updated[app][module] = {};

      updated[app][module][action] = checked;

      // After updating the action, ensure state updates only; no aggregates needed

      // If all actions checked, ensure module is fully checked (implicitly handled by isModuleFullyChecked)
      // If not all actions checked, module is not fully checked

      // Check if all modules in the app are fully checked
      // No extra aggregate computations needed here

      // No explicit state for module or app level checkboxes in selected, so no need to update separately
      // The parent checkboxes derive their checked state from isModuleFullyChecked and isAppFullyChecked

      return updated;
    });
  };

  const handleAppAllChange = (app: string, checked: boolean) => {
    const appData = modules.find((mod: any) => mod.app_name === app);

    setSelected((prev: any) => {
      const updated = structuredClone(prev || {});
      updated[app] = {};

      Object.entries(appData.module_name_perm).forEach(
        ([modName, actions]: [string, any]) => {
          updated[app][modName] = {};
          actions.forEach((action: string) => {
            updated[app][modName][action] = checked;
          });
        }
      );

      return updated;
    });
  };

  const handleModuleAllChange = (
    app: string,
    module: string,
    checked: boolean
  ) => {
    setSelected((prev: any) => {
      // const updated = { ...prev };
      const updated = structuredClone(prev || {});

      updated[app] = updated[app] || {};
      updated[app][module] = {};
      const actions =
        modules.find((m: any) => m.app_name === app)?.module_name_perm[
          module
        ] || [];
      actions.forEach((action: string) => {
        updated[app][module][action] = checked;
      });
      return updated;
    });
  };

  const handleGlobalAllChange = (checked: boolean) => {
    const updated: any = {};
    modules.forEach((app: any) => {
      updated[app.app_name] = {};
      Object.entries(app.module_name_perm).forEach(
        ([modName, actions]: [string, any]) => {
          updated[app.app_name][modName] = {};
          actions.forEach((action: string) => {
            updated[app.app_name][modName][action] = checked;
          });
        }
      );
    });
    setSelected(updated);
  };

  const isModuleFullyChecked = (app: string, module: string) => {
    const actions =
      modules.find((m: any) => m.app_name === app)?.module_name_perm[module] ||
      [];
    if (!selected?.[app] || !selected[app][module]) {
      return false;
    }
    return actions.every(
      (action: string) => selected[app][module]?.[action] === true
    );
  };

  const isAppFullyChecked = (app: string) => {
    const appData = modules.find((m: any) => m.app_name === app);
    if (!selected?.[app]) {
      return false;
    }
    return Object.keys(appData?.module_name_perm || {}).every((modName) =>
      isModuleFullyChecked(app, modName)
    );
  };

  const isGlobalFullyChecked = () => {
    if (!modules || modules.length === 0) {
      return false;
    }
    return modules.every((app: any) => isAppFullyChecked(app.app_name));
  };

  const updatePermision = async (payload: any) => {
    console.log("All the permission Updating: ", payload);
    try {
      const update = await Axios.put(
        `/permissions/permissions_list/update/${user}/`,
        payload
      );
      return update.data;
    } catch (error) {
      console.error(error);
    }
  };

  const createPermision = async (payload: any) => {
    try {
      const create = await Axios.post(
        `/permissions/permissions_list/create/`,
        payload
      );
      return create.data;
    } catch (error) {
      console.error(error);
    }
  };

  // Helper function to delete all permissions for a user using the delete endpoint
  const handleSubmitWithEmptyPermissions = async () => {
    setIsSubmitting(true);
    
    // Extract user ID - ensure it's a string
    let userId: string = "";
    if (typeof user === 'string') {
      userId = user;
    } else if (user?.user_id) {
      userId = user.user_id;
    } else if (user?.id) {
      userId = user.id;
    } else if (authUser?.user_id) {
      userId = authUser.user_id;
    } else {
      userId = String(user || "");
    }
    
    if (!userId) {
      toast.error("User ID is required. Please ensure you're creating/editing a valid user.");
      setIsSubmitting(false);
      return;
    }

    try {
      // Use the delete endpoint to remove all permissions
      const response = await Axios.delete(
        `/permissions/permissions_list/delete-all/${userId}/`
      );

      // Handle response
      const responseData = response?.data || response;
      const httpStatus = response?.status || responseData?.status;
      const isSuccess = httpStatus === 200 || responseData?.success === true;

      if (isSuccess) {
        // After successful deletion, mark as not having permissions
        setHasExistingPermissions(false);
        setSelected({}); // Clear selected state
        permissionsFetchedRef.current = null; // Reset fetch ref
        
        toast.success(responseData?.message || "All permissions removed successfully");
      } else {
        toast.error(
          responseData?.message || 
          "Unable to delete permissions"
        );
      }
    } catch (error: any) {
      console.error("Permission removal error:", error);
      
      const errorMessage = 
        error?.response?.data?.message || 
        error?.response?.data?.error || 
        error?.message || 
        "Unknown error";
      
      toast.error(`Failed to remove permissions: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    // Filter out modules with no permissions (all false or empty)
    const apps_permissions = Object.entries(selected)
      .map(([apps_name, modulesObj]: any) => {
        const modules_permissions = Object.entries(modulesObj)
          .map(([modules_name, permissions]: any) => {
            // Filter out permissions that are false, keep only true values
            const filteredPermissions: any = {};
            Object.entries(permissions || {}).forEach(([key, value]) => {
              if (value === true) {
                filteredPermissions[key] = value;
              }
            });
            
            // Only include module if it has at least one permission set to true
            if (Object.keys(filteredPermissions).length > 0) {
              return {
                modules_name,
                permissions: filteredPermissions,
              };
            }
            return null;
          })
          .filter((module: any) => module !== null); // Remove null entries
        
        // Only include app if it has at least one module with permissions
        if (modules_permissions.length > 0) {
          return {
            apps_name,
            modules_permissions,
          };
        }
        return null;
      })
      .filter((app: any) => app !== null); // Remove null entries

    // If no permissions selected, show confirmation dialog to remove all permissions
    if (apps_permissions.length === 0) {
      setIsSubmitting(false);
      
      showCustomSwal({
        title: "Remove All Permissions?",
        text: "No permissions are selected. Do you want to remove all permissions for this user?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#dc3545",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Yes, remove all permissions",
        cancelButtonText: "Cancel",
      }).then((result) => {
        if (result.isConfirmed) {
          // User confirmed - proceed with empty permissions array (will delete all permissions)
          handleSubmitWithEmptyPermissions();
        }
        // If cancelled, do nothing (user can select permissions)
      });
      
      return;
    }

    // Extract user ID - ensure it's a string
    let userId: string = "";
    if (typeof user === 'string') {
      userId = user;
    } else if (user?.user_id) {
      userId = user.user_id;
    } else if (user?.id) {
      userId = user.id;
    } else if (authUser?.user_id) {
      userId = authUser.user_id;
    } else {
      userId = String(user || "");
    }
    
    if (!userId) {
      toast.error("User ID is required. Please ensure you're creating/editing a valid user.");
      setIsSubmitting(false);
      return;
    }

    // Backend will automatically set created_by and updated_by from authenticated user
    const payload = {
      user_id: userId, // Standardized: always use user_id
      apps_permissions,
    };

    let response = null;

    try {
      // If user has existing permissions, update. Otherwise, create.
      if (hasExistingPermissions) {
        response = await updatePermision(payload);
      } else {
        response = await createPermision(payload);
      }

      // Handle response - Axios wraps the response in .data
      // Backend returns: { status, success, message, data }
      const responseData = response?.data || response;
      const httpStatus = response?.status || responseData?.status;
      const isSuccess = httpStatus === 200 || httpStatus === 201 || responseData?.success === true;

      if (isSuccess) {
        // After successful create, mark as having existing permissions
        if (!hasExistingPermissions) {
          setHasExistingPermissions(true);
          // Update the ref so we know permissions now exist
          permissionsFetchedRef.current = String(user);
        }
        
        toast.success(responseData?.message || "Permissions saved successfully");
        setTimeout(() => {
          // navigate("/user-list");
        }, 2000);
      } else {
        toast.error(
          responseData?.message || 
          `Unable to ${hasExistingPermissions ? "update" : "create"} permission`
        );
      }
    } catch (error: any) {
      console.error("Permission save error:", error);
      
      // Extract error message from Axios error response
      const errorMessage = 
        error?.response?.data?.message || 
        error?.response?.data?.error || 
        error?.message || 
        "Unknown error";
      
      toast.error(
        `Failed to ${
          hasExistingPermissions ? "update" : "create"
        } permission: ${errorMessage}`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  console.log(modules)

  return (
    <div className="mt-3 bg-white py-2 rounded-4 shadow-sm">
      <h5 className="mb-3">Permissions</h5>

      <LoaderErrorWrapper loading={loading} error={error}>
        <>
          <Form.Group className="mb-3" controlId="globalAll">
            <Form.Check
              type="checkbox"
              label="All Permissions"
              checked={isGlobalFullyChecked()}
              onChange={(e) => handleGlobalAllChange(e.target.checked)}
            />
          </Form.Group>

          {modules.map((app: any, idx: number) => (
            <div key={idx} className="mb-3">
              <Accordion key={idx} activeKey={openAccordion}>
                <Accordion.Item eventKey={app.app_name}>
                  <Accordion.Header
                    onClick={() => toggleAccordion(app.app_name)}
                  >
                    {app.app_name}
                  </Accordion.Header>
                  <Accordion.Body>
                    <Form.Group className="mb-2">
                      <Form.Check
                        type="checkbox"
                        label="All Permissions"
                        checked={isAppFullyChecked(app.app_name)}
                        onChange={(e) =>
                          handleAppAllChange(app.app_name, e.target.checked)
                        }
                      />
                    </Form.Group>
                    {Object.entries(app.module_name_perm)
                      .filter(([modName]) => {
                        // Filter out specific modules when app_name is "User Management"
                        if (app.app_name === "User Management") {
                          const hiddenModules = [
                            "Organisation",
                            "Department",
                            "Team",
                            "User Types",
                            "Module",
                          ];
                          return !hiddenModules.includes(modName);
                        }
                        return true;
                      })
                      .map(([modName, actions]: [string, any], i: number) => (
                        <div key={i} className="ms-3 mt-3">
                          <strong>{modName}</strong>
                          <div className="ms-3 mb-2">
                            <Form.Check
                              type="checkbox"
                              label="All Actions"
                              checked={isModuleFullyChecked(
                                app.app_name,
                                modName
                              )}
                              onChange={(e) =>
                                handleModuleAllChange(
                                  app.app_name,
                                  modName,
                                  e.target.checked
                                )
                              }
                            />
                          </div>
                          <div className="ms-3 d-flex flex-wrap gap-3 mt-2">
                            {actions.map((action: string, j: number) => (
                              <Form.Check
                                key={j}
                                type="checkbox"
                                inline
                                label={action}
                                checked={
                                  selected?.[app.app_name]?.[modName]?.[
                                    action
                                  ] || false
                                }
                                onChange={(e) =>
                                  handlePermissionChange(
                                    app.app_name,
                                    modName,
                                    action,
                                    e.target.checked
                                  )
                                }
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </div>
          ))}

          <div className="d-flex justify-content-center gap-3 py-3">
            <CancelBackButton to="/user-list" />
            <SubmitButton
              isSubmitting={isSubmitting}
              isEdit={hasExistingPermissions}
              onClick={() => void handleSubmit()}
              labels={{
                create: "Create Permissions",
                update: "Update",
                submitting: "Saving...",
                updating: "Saving...",
              }}
            />
          </div>
        </>
      </LoaderErrorWrapper>
    </div>
  );
};

export default PermissionTab;
